import React, { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { useClientId } from '../../../../../../_hooks/useClientId';
import { SelectAccountToBeDeleted } from '../../AccountPreferences.meta';
import { Brokerage } from './forms/Brokerage/Brokerage';
import { Checking } from './forms/Checking/Checking';
import { DefaultForm } from './forms/DefaultForm/DefaultForm';
import { Savings } from './forms/Savings/Savings';
import { TaxFree } from './forms/TaxFree/TaxFree';
import { TraditionalIRA } from './forms/TraditionalIRA/TraditionalRA';
import { useAccountForm } from './useAccountForm';
import { InheritedIRA } from './forms/InheritedIRA/InheritedIRA';
import { CollegeSavingsPlan } from './forms/CollegeSavingsPlan/CollegeSavingsPlan';
import { AccountFormProvider, Alert } from './context/AccountFormProvider';
import { Annuity } from './forms/Annuity/Annuity';
import { SepIRA } from './forms/SepIRA/SepIRA';
import { SimpleIRA } from './forms/SimpleIRA/SimpleIRA';
import { EmployerPlan } from './forms/EmployerPlan/EmployerPlan';
import { FourHundredK } from './forms/FourHundredK/FourHundredK';
import { CreditCard } from './forms/CreditCard/CreditCard';
import { Mortgage } from './forms/Mortgage/Mortgage';
import { OtherLoans } from './forms/OtherLoans/OtherLoans';
import {
  AccountPortfolioDTO,
  useAccountPortfolioQuery,
} from '../../../../api/useAccountPortfolioQuery';
import { FullPageLoading } from '../../../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { MESSAGES } from '../../../../../../CommonConstants';
import { toast } from '../../../../../../CommonComponents/OPL/Toast/Toast';
import { TaxDeferred } from './forms/TaxDeferred/TaxDeferred';
import { HSA } from './forms/HSA/HSA';
import { useAccountsQuery } from '../../../../api/useAccountsQuery';
import { AccountType, useAccountsOptionsQuery } from '../../../../api/useAccountsOptionsQuery';
import { AccountFormError } from './AccountForm.error';
import { AccountFormLoading } from './AccountForm.loading';
import { DonorAdvisedFund } from './forms/DonorAdvisedFund/DonorAdvisedFund';
import {
  AccountModalUsageContext,
  AccountModalUsageContextOptions,
} from './context/AccountFormProvider.meta';
import {
  accountPortfolioFormToBody,
  bankAccountsFormToBody,
  LocalSaveOptions,
} from './AccountForm.meta';
import { useAccountFormContext } from './context/useAccountFormContext';

const FORM_COMPONENT_DICT = {
  [AccountType.Checking]: Checking,
  [AccountType.Savings]: Savings,
  [AccountType.CD]: Checking,
  [AccountType.Brokerage]: Brokerage,
  [AccountType.IRA]: TraditionalIRA,
  [AccountType.Roth]: TraditionalIRA,
  [AccountType.TaxFree]: TaxFree,
  [AccountType.InheritedIra]: InheritedIRA,
  [AccountType.InheritedRoth]: InheritedIRA,
  [AccountType.E529]: CollegeSavingsPlan,
  [AccountType.Annuity]: Annuity,
  [AccountType.SepIra]: SepIRA,
  [AccountType.SimpleIra]: SimpleIRA,
  [AccountType.EmployerPlan]: EmployerPlan,
  [AccountType.E401K]: FourHundredK,
  [AccountType.Roth401K]: FourHundredK,
  [AccountType.E401A]: FourHundredK,
  [AccountType.E403B]: FourHundredK,
  [AccountType.E457]: FourHundredK,
  [AccountType.CreditCard]: CreditCard,
  [AccountType.Mortgage]: Mortgage,
  [AccountType.Installment]: OtherLoans,
  [AccountType.LineOfCredit]: OtherLoans,
  [AccountType.StudentLoan]: OtherLoans,
  [AccountType.AutoLoan]: OtherLoans,
  [AccountType.HSA]: HSA,
  [AccountType.TaxDeferred]: TaxDeferred,
  [AccountType.DonorAdvisedFund]: DonorAdvisedFund,
};

interface Props {
  onClose(): void;
  accountId?: string;
  onDeleteAccount?: React.Dispatch<React.SetStateAction<SelectAccountToBeDeleted>>;
  tabSelected?: string;
  usageContext?: AccountModalUsageContext;
  usageContextOptions?: AccountModalUsageContextOptions;
  localSaveOptions?: LocalSaveOptions;
}
export const AccountForm = (props: Props) => {
  const { accountId, onClose, usageContext, usageContextOptions, localSaveOptions } = props;

  const [alert, setAlert] = useState<Alert | null>(null);

  const {
    data: accountQueryData,
    isError: accountQueryDataIsError,
    isFetching: accountQueryDataIsFetching,
    refetch: accountQueryDataRefetch,
  } = useAccountsQuery();

  const {
    data: accountOptionsData,
    isError: accountOptionsQueryDataIsError,
    isFetching: accountOptionsQueryDataIsFetching,
    refetch: accountOptionsQueryDataIsRefetch,
  } = useAccountsOptionsQuery({ usageContext, usageContextOptions });

  if (accountQueryDataIsFetching || accountOptionsQueryDataIsFetching) {
    return <AccountFormLoading />;
  }

  if (accountQueryDataIsError || accountOptionsQueryDataIsError) {
    return (
      <AccountFormError
        accountId={accountId}
        onClose={onClose}
        onRetry={() => {
          return accountQueryDataIsError
            ? accountQueryDataRefetch()
            : accountOptionsQueryDataIsRefetch();
        }}
      />
    );
  }

  if (!accountQueryData || !accountOptionsData) {
    return null;
  }

  return (
    <AccountFormProvider
      value={{
        alert,
        setAlert,
        accountId,
        onClose,
        accounts: accountQueryData.Accounts,
        options: accountOptionsData.Options,
        usageContext,
        usageContextOptions,
        localSaveOptions,
      }}
    >
      {accountId ? <EditAccountForm {...props} /> : <AccountFormContent {...props} />}
    </AccountFormProvider>
  );
};

const EditAccountForm = (props: Props) => {
  const { accountId } = props;

  const { isLoading, data: accountPortfolioData } = useAccountPortfolioQuery({
    accountId,
    onError() {
      toast.error(MESSAGES.GENERIC_ERROR);
    },
  });

  if (isLoading) {
    return <FullPageLoading />;
  }

  if (!accountPortfolioData) {
    return null;
  }

  return <AccountFormContent {...props} accountPortfolioData={accountPortfolioData} />;
};

interface ContentProps extends Props {
  accountPortfolioData?: AccountPortfolioDTO;
}

const AccountFormContent = ({
  onClose,
  accountId,
  onDeleteAccount,
  tabSelected,
  accountPortfolioData,
}: ContentProps) => {
  const clientId = useClientId();

  const { localSaveOptions } = useAccountFormContext();

  const {
    data: { errors, isSaving, formBag },
    actions: { saveAccount },
  } = useAccountForm({ accountId, accountPortfolioData });

  const { watch } = formBag;

  const [accountType, accountName, isLinked] = watch([
    'bankAccount.AccountType',
    'bankAccount.AccountName',
    'bankAccount.IsLinked',
  ]);

  const onSubmit: React.FormEventHandler<HTMLFormElement> = evt => {
    if (evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }

    return formBag.handleSubmit(async data => {
      try {
        if (localSaveOptions) {
          localSaveOptions.onSave({
            bankAccount: bankAccountsFormToBody(data.bankAccount),
            accountPortfolio: accountPortfolioFormToBody(data.accountPortfolio),
          });

          toast.success('Account details successfully added.');
        } else {
          await saveAccount(data);
        }

        onClose();

        if (!localSaveOptions) {
          toast.success('Account saved successfully.');
        }
      } catch (err) {
        console.error(err);
      }
    })(evt);
  };

  const FormComponent = accountType !== null ? FORM_COMPONENT_DICT[accountType] : DefaultForm;

  return (
    <Modal
      size="xl"
      isOpen
      onRequestClose={onClose}
      aria={{ labelledby: 'account-modal-form' }}
      shouldCloseOnOverlayClick={false}
    >
      <ModalHeader id="account-modal-form">{accountId ? 'Edit' : 'Add'} Account</ModalHeader>

      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        <FormProvider {...formBag}>
          <form onSubmit={onSubmit} id="account-form">
            <FormComponent
              defaultSelectedTab={tabSelected}
              accountType={accountType}
              errorMessage={errors?.message}
              accountId={accountId}
            />
          </form>
        </FormProvider>
      </ModalBody>

      <ModalFooter>
        {accountId && onDeleteAccount && !isLinked && (
          <Button
            variant="errorSecondary"
            onClick={() => {
              onDeleteAccount({
                accountId,
                accountName: accountName || '',
                clientId,
              });
            }}
          >
            Delete
          </Button>
        )}

        <Button variant="subtleInactive" onClick={onClose} ml="auto">
          Cancel
        </Button>

        <Button ml="8" type="submit" form="account-form" disabled={isSaving} isLoading={isSaving}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};
